/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import PendoBeacon from './beacons/PendoBeacon';
import Logger from './logger/PerformanceMonitorLogger';
import initializeServiceMonitor from './monitors/ServiceMonitor';
import {IBeaconClient} from './types/types';

/**
 * Provides the Application Performance Monitor Framework API from which all
 * APM configurations are to be centrally managed.
 *
 * The APM framework is only be initialized once via the `initialize` method,
 * which allows for initializing the APM framework, including the globally
 * enabling or disabling of all Performance Monitoring APIs. Typically, this
 * method is only to be invoked immediately after application pre-initialization
 * has been completed based on an environment configuration, feature flag, build
 * configuration, or after the underlying metrics services have been initialized.
 * In addition, when initialized with `enabled` set to `true`, the underlying
 * Beacon client will be configured for subsequent use for sending APM metrics
 * to an external service.
 *
 * ```typescript
 *   // _app.tsx
 *   import APM from '@ion/telemetry/src/apm/APM';
import {autoMonitorPagePerformance} from '@ion/telemetry/src/apm/automation/PagePerformanceAutoMonitor';
 *   ...
 *
 *   useEffect(() => {
 *     ...
 *     if (...) {
 *       // initialize the APM Framework ...
 *       APM.initialize(true);
 *       // initialize automatic page performance monitoring ...
 *       autoMonitorPagePerformance();
 *     }
 *   }, [...];
 * ```
 */
export default class APM {
  /**
   * Static flag indicating whether global performance monitoring is enabled
   * for the application.
   */
  protected static _enabled = false;

  /**
   * Static flag indicating whether emissions to the beacon client are to be
   * made (defaults to `true`). When the `initialize` method is invoked, if
   * the `enableEmission` argument is set to `false`, getting and setting the
   * beacon will be bypassed.
   */
  protected static _enableEmission = false;

  /**
   * Provides a reference to the `IBeaconClient` implementation to which all
   * all Application Performance Monitoring Tracking Events are to be sent.
   */
  protected static _beaconClient: IBeaconClient;

  /**
   * Provides a static `enabled` getter which determines if Application Performance
   * Monitoring is globally enabled.
   *
   * ```typescript
   *   import APM from '@ion/telemetry/src/apm/config/APM';
   *   ...
   *
   *   if (APM.isEnabled) {
   *     ...
   *   }
   * ```
   *
   * @returns `true` if the monitoring is enabled, otherwise `false`.
   */
  static get isEnabled(): boolean {
    return this._enabled;
  }

  /**
   * Provides a static method which returns the internally configured
   * `IBeaconClient` instance to which all Application Performance
   * Monitoring Events are to be sent.
   *
   * ```typescript
   *   import APM from '@ion/telemetry/src/apm/config/APM';
   *   ...
   *
   *   const client: IBeaconClient = APM.getBeaconClient();
   * ```
   *
   * @returns `true` if the monitoring is enabled, otherwise `false`.
   */
  static getBeaconClient(): IBeaconClient {
    if (this._enableEmission) {
      return this._beaconClient;
    }
  }

  /**
   * Provides a static method which allows for setting the `IBeaconClient`
   * instance to which all Application Performance Monitoring Events are
   * to be sent.
   *
   * ```typescript
   *   import APM from '@ion/telemetry/src/apm/config/APM';
   *   ...
   *
   *   APM.setBeaconClient(client);
   * ```
   *
   * @returns `true` if the monitoring is enabled, otherwise `false`.
   */
  static setBeaconClient(client: IBeaconClient) {
    if (this._enableEmission) {
      this._beaconClient = client;
    }
  }

  /**
   * Provides a static `initialization` method which allows for initializing the
   * APM framework, including the globally enabling or disabling of all Performance
   * Monitoring APIs. Typically, this getter is only to be set immediately after
   * application pre-initialization based on an environment configuration, build
   * configuration, or after the underlying metrics services have been initialized.
   * In addition, when initialized with `enabled` set to `true`, the underlying
   * Beacon client will be configured for subsequent use for sending APM metrics
   * to an external service.
   *
   * ```typescript
   *   // _app.tsx
   *   import APM from '@ion/telemetry/src/apm/APM';
   *   import {autoMonitorPagePerformance} from '@ion/telemetry/src/apm/automation/PagePerformanceAutoMonitor';
   *   ...
   *
   *   useEffect(() => {
   *     loadConfig(config => {
   *       if (enableAPM) {
   *         // initialize and enable apm framework ...
   *         APM.initialize(true);
   *
   *         // or, initialize with a specific Beacon client implementation ...
   *         APM.initialize(true, client);
   *
   *         // or, initialize the framework but omit emitting to a external endpoint ...
   *         APM.initialize(true, null, false);
   *
   *         // initialize page performance auto-monitoring ...
   *         autoMonitorPagePerformance();
   *       }
   *     }, false);
   *   }, [...]);
   *
   * ```
   *
   * @param enabled `true` if the monitoring is to be enabled, otherwise `false`.
   * @param client an optional `IBeaconClient` implementation to which all APM
   *        Events are to be sent.
   * @param enableEmission when `true` (default), the APM framework will emit all
   *        events to the configured Beacon Client, otherwise, the framework will
   *        only log monitoring results via the Logging API. This parameter can be
   *        used by developers to discretely monitor application performance metrics
   *        without emitting the results to an external endpoint.
   */
  static initialize(enabled: boolean, client?: IBeaconClient, enableEmission = true) {
    this._enabled = enabled;
    this._enableEmission = enableEmission;
    if (enabled) {
      if (enableEmission && (client || !this._beaconClient)) {
        this._beaconClient = client || PendoBeacon;
      }
      initializeServiceMonitor();
    } else {
      this._beaconClient = undefined;
    }
    Logger.isEnabled && Logger.monitoringEnabled(enabled, enableEmission);
  }
}
